import { z } from "zod";
import { IsoDateTimeSchema } from "./common";
import { BookIdSchema, ChapterIdSchema, InstructionIdSchema, VoiceIdSchema } from "./ids";
import { StyleSheetOriginSchema } from "./states";

/*
 * The content mirrors STYLE_SHEET_SCHEMA in scripts/evaluate_refinement.py
 * (lines 64-99) field for field, snake_case included (say_as, ipa), because
 * the same schema validates the LLM's output, the stored jsonb, and the wire.
 * Every object is strict, as the Python schema sets additionalProperties false.
 */

/** One spelling that appears in the Book (a plural, a genitive, a surname alone) with its own pronunciation. */
export const StyleSheetFormSchema = z.strictObject({
  spelling: z.string(),
  say_as: z.string(),
  ipa: z.string().nullable(),
});
export type StyleSheetForm = z.infer<typeof StyleSheetFormSchema>;

export const StyleSheetTermSchema = z.strictObject({
  term: z.string(),
  forms: z.array(StyleSheetFormSchema),
  say_as: z.string(),
  ipa: z.string().nullable(),
  note: z.string(),
});
export type StyleSheetTerm = z.infer<typeof StyleSheetTermSchema>;

export const StyleSheetConventionSchema = z.strictObject({
  topic: z.string(),
  rule: z.string(),
});
export type StyleSheetConvention = z.infer<typeof StyleSheetConventionSchema>;

export const StyleSheetBookFactsSchema = z.strictObject({
  genre: z.string(),
  tone: z.string(),
  language: z.string(),
});
export type StyleSheetBookFacts = z.infer<typeof StyleSheetBookFactsSchema>;

export const StyleSheetContentSchema = z.strictObject({
  book: StyleSheetBookFactsSchema,
  conventions: z.array(StyleSheetConventionSchema),
  terms: z.array(StyleSheetTermSchema),
  /** The Instruction texts as Refinement sees them; RecordedInstruction carries the Chapter each came from. */
  instructions: z.array(z.string()),
});
export type StyleSheetContent = z.infer<typeof StyleSheetContentSchema>;

/** An Instruction as recorded: "added from chapter 3 · applies to every later run". */
export const RecordedInstructionSchema = z.strictObject({
  id: InstructionIdSchema,
  text: z.string(),
  chapterId: ChapterIdSchema.nullable(),
  /** The included-only display number of that Chapter ("added from chapter 3"). */
  chapterNumber: z.int().positive().nullable(),
  chapterTitle: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type RecordedInstruction = z.infer<typeof RecordedInstructionSchema>;

/** The Voice a Pronunciation Check speaks in: the Narrator once cast, the language default before. */
export const CheckVoiceSchema = z.strictObject({
  voiceId: VoiceIdSchema,
  name: z.string(),
  source: z.enum(["narrator", "languageDefault"]),
});
export type CheckVoice = z.infer<typeof CheckVoiceSchema>;

export const StyleSheetSchema = z.strictObject({
  bookId: BookIdSchema,
  /** Monotonic per Book; PUT sends it back as baseVersion and gets 409 STYLE_SHEET_CONFLICT when it moved. */
  version: z.int().positive(),
  origin: StyleSheetOriginSchema,
  content: StyleSheetContentSchema,
  instructions: z.array(RecordedInstructionSchema),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  /** Chapters a "re-run unapproved" would touch (refined or in review, not Approved). */
  unapprovedChapterCount: z.int().nonnegative(),
  checkVoice: CheckVoiceSchema.nullable(),
});
export type StyleSheet = z.infer<typeof StyleSheetSchema>;

/** PUT /books/:id/style-sheet. Instructions are appended by re-runs and never replaced here. */
export const StyleSheetPutSchema = z.strictObject({
  baseVersion: z.int().positive(),
  book: StyleSheetBookFactsSchema,
  conventions: z.array(StyleSheetConventionSchema),
  terms: z.array(StyleSheetTermSchema),
});
export type StyleSheetPut = z.infer<typeof StyleSheetPutSchema>;

/** POST /books/:id/style-sheet/rerun-unapproved. */
export const RerunUnapprovedResponseSchema = z.strictObject({
  bookId: BookIdSchema,
  styleSheetVersion: z.int().positive(),
  chapterIds: z.array(ChapterIdSchema),
});
export type RerunUnapprovedResponse = z.infer<typeof RerunUnapprovedResponseSchema>;

/**
 * POST /books/:id/style-sheet/terms/:termId/check. Terms carry no id of their
 * own in STYLE_SHEET_SCHEMA, so :termId is the term's index in `content.terms`
 * of `baseVersion`; a moved version answers 409 STYLE_SHEET_CONFLICT.
 */
export const PronunciationCheckRequestSchema = z.strictObject({
  voiceId: VoiceIdSchema.optional(),
  draft: StyleSheetPutSchema.optional(),
  baseVersion: z.int().positive(),
});
export type PronunciationCheckRequest = z.infer<typeof PronunciationCheckRequestSchema>;

/** Drafts may contain unfinished rows; activating them requires named terms and complete conventions. */
export const guidanceCanApply = (draft: StyleSheetPut): boolean =>
  draft.terms.every(
    (term) =>
      term.term.trim().length > 0 && term.forms.every((form) => form.spelling.trim().length > 0),
  ) &&
  draft.conventions.every((rule) => rule.topic.trim().length > 0 && rule.rule.trim().length > 0);
